import React from "react";
import Map from "./Map";
import { FeatureCollection } from "geojson";
import { useEffect, useState } from "react";
import { API_BASE_URL, SessionManager } from "../../utils/consts";
import GeojsonSwitch from "./GeojsonSwitch";

import {
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";

interface DemoData {
  asphalt_geojson: FeatureCollection | null;
  building_geojson: FeatureCollection | null;
  concrete_geojson: FeatureCollection | null;
  edging_geojson: FeatureCollection | null;
  grass_geojson: FeatureCollection | null;
  low_vegetation_geojson: FeatureCollection | null;
  medium_high_vegetation_geojson: FeatureCollection | null;
  muddy_geojson: FeatureCollection | null;
  natural_ground_geojson: FeatureCollection | null;
  water_geojson: FeatureCollection | null;
}

interface DashboardProps {
  sessionManager: SessionManager;
}

async function fetchDemoData(token: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/demo`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Accept-Encoding": "gzip",
      },
      // responseType: "arraybuffer",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const jsonData = await response.json();
    console.log(jsonData);
    return jsonData;
  } catch (error) {
    console.error("Error fetching demo data:", error);
  }
}

const Dashboard: React.FC<DashboardProps> = ({ sessionManager }) => {
  const [data, setData] = useState<DemoData | null>(null);
  const [asphalt_geojson, setAsphaltGeojson] =
    useState<FeatureCollection | null>(null);
  const [building_geojson, setBuildingGeojson] =
    useState<FeatureCollection | null>(null);
  const [concrete_geojson, setConcreteGeojson] =
    useState<FeatureCollection | null>(null);
  const [edging_geojson, setEdgingGeojson] = useState<FeatureCollection | null>(
    null
  );
  const [grass_geojson, setGrassGeojson] = useState<FeatureCollection | null>(
    null
  );
  const [low_vegetation_geojson, setLowVegetationGeojson] =
    useState<FeatureCollection | null>(null);
  const [medium_high_vegetation_geojson, setMediumHighVegetationGeojson] =
    useState<FeatureCollection | null>(null);
  const [muddy_geojson, setMuddyGeojson] = useState<FeatureCollection | null>(
    null
  );
  const [natural_ground_geojson, setNaturalGroundGeojson] =
    useState<FeatureCollection | null>(null);
  const [water_geojson, setWaterGeojson] = useState<FeatureCollection | null>(
    null
  );
  const [grassAreas, setGrassAreas] = useState<{ [key: string]: number }>({
    "0_5": 0,
    "5_15": 0,
    "15_25": 0,
    "25_40": 0,
    total: 0,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      const token = sessionManager.getToken();
      if (!token) {
        sessionManager.signOut();
      } else {
        const response = (await fetchDemoData(token)) as DemoData;
        setData(response);
        // setAsphaltGeojson(response.asphalt_geojson);
        // setBuildingGeojson(response.building_geojson);
        // setConcreteGeojson(response.concrete_geojson);
        // setEdgingGeojson(response.edging_geojson);
        setGrassGeojson(response.grass_geojson);
        // setLowVegetationGeojson(response.low_vegetation_geojson);
        // setMediumHighVegetationGeojson(response.medium_high_vegetation_geojson);
        // setMuddyGeojson(response.muddy_geojson);
        // setNaturalGroundGeojson(response.natural_ground_geojson);
        // setWaterGeojson(response.water_geojson);
        setGrassAreas(grassArea(response.grass_geojson));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const grassArea = (grass_geojson: FeatureCollection | null) => {
    if (!grass_geojson) return {};

    const areaGroups: { [key: string]: number } = {
      "0_5": 0,
      "5_15": 0,
      "15_25": 0,
      "25_40": 0,
      total: 0,
    };

    grass_geojson.features.forEach((feature) => {
      const { slope_lower, slope_upper, area } = feature.properties as {
        slope_lower: number;
        slope_upper: number;
        area: number;
      };
      if (slope_lower >= 0 && slope_upper <= 5) {
        areaGroups["0_5"] += area;
      } else if (slope_lower >= 5 && slope_upper <= 15) {
        areaGroups["5_15"] += area;
      } else if (slope_lower >= 15 && slope_upper <= 25) {
        areaGroups["15_25"] += area;
      } else if (slope_lower >= 25 && slope_upper <= 40) {
        areaGroups["25_40"] += area;
      }
      areaGroups.total += area;
    });

    return areaGroups;
  };

  return (
    <div style={{ display: "flex" }}>
      {data === null && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 2000,
          }}
        >
          <CircularProgress size={100} />
        </div>
      )}
      <div
        style={{
          position: "fixed",
          top: 200,
          left: 12,
          zIndex: 1300,
          backgroundColor: "#f0f0f0",
          marginLeft: drawerOpen ? "300px" : "0",
        }}
      >
        <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
          <MenuIcon />
        </IconButton>
      </div>
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        style={{ width: "auto", top: "100px" }}
      >
        <div
          style={{
            // width: "300px",
            padding: "10px",
            borderRight: "1px solid #ccc",
          }}
        >
          <h3>Legend</h3>
          <h4>Demo Data</h4>
          <List>
            <ListItem>
              <GeojsonSwitch
                label="Grass"
                geojson={grass_geojson}
                setGeojson={setGrassGeojson}
                data={data?.grass_geojson ?? null}
              />
            </ListItem>
            <ListItemText primary="Grass Area Legend" />
            <ListItem>
              <ul style={{ listStyleType: "none", padding: 0 }}>
                <li
                  style={{
                    marginBottom: "5px",
                    color: "#03a108",
                    fontWeight: 1000,
                  }}
                >
                  0-5%:{" "}
                  {grassAreas["0_5"].toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  m²
                </li>
                <li
                  style={{
                    marginBottom: "5px",
                    color: "#7edc03",
                    fontWeight: 1000,
                  }}
                >
                  5-15%:{" "}
                  {grassAreas["5_15"].toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  m²
                </li>
                <li
                  style={{
                    marginBottom: "5px",
                    color: "#d1d404",
                    fontWeight: 1000,
                  }}
                >
                  15-25%:{" "}
                  {grassAreas["15_25"].toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  m²
                </li>
                <li
                  style={{
                    marginBottom: "5px",
                    color: "#e87c00",
                    fontWeight: 1000,
                  }}
                >
                  25-40%:{" "}
                  {grassAreas["25_40"].toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  m²
                </li>
                <li style={{ marginBottom: "5px", fontWeight: 1000 }}>
                  Total:{" "}
                  {grassAreas.total.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  m²
                </li>
              </ul>
            </ListItem>
            <ListItem>
              <GeojsonSwitch
                label="Asphalt"
                geojson={asphalt_geojson}
                setGeojson={setAsphaltGeojson}
                data={data?.asphalt_geojson ?? null}
              />
            </ListItem>
            <ListItem>
              <GeojsonSwitch
                label="Building"
                geojson={building_geojson}
                setGeojson={setBuildingGeojson}
                data={data?.building_geojson ?? null}
              />
            </ListItem>
            <ListItem>
              <GeojsonSwitch
                label="Concrete"
                geojson={concrete_geojson}
                setGeojson={setConcreteGeojson}
                data={data?.concrete_geojson ?? null}
              />
            </ListItem>
            <ListItem>
              <GeojsonSwitch
                label="Edging"
                geojson={edging_geojson}
                setGeojson={setEdgingGeojson}
                data={data?.edging_geojson ?? null}
              />
            </ListItem>
            <ListItem>
              <GeojsonSwitch
                label="Low Vegetation"
                geojson={low_vegetation_geojson}
                setGeojson={setLowVegetationGeojson}
                data={data?.low_vegetation_geojson ?? null}
              />
            </ListItem>
            <ListItem>
              <GeojsonSwitch
                label="Medium High Vegetation"
                geojson={medium_high_vegetation_geojson}
                setGeojson={setMediumHighVegetationGeojson}
                data={data?.medium_high_vegetation_geojson ?? null}
              />
            </ListItem>
            <ListItem>
              <GeojsonSwitch
                label="Muddy"
                geojson={muddy_geojson}
                setGeojson={setMuddyGeojson}
                data={data?.muddy_geojson ?? null}
              />
            </ListItem>
            <ListItem>
              <GeojsonSwitch
                label="Natural Ground"
                geojson={natural_ground_geojson}
                setGeojson={setNaturalGroundGeojson}
                data={data?.natural_ground_geojson ?? null}
              />
            </ListItem>
            <ListItem>
              <GeojsonSwitch
                label="Water"
                geojson={water_geojson}
                setGeojson={setWaterGeojson}
                data={data?.water_geojson ?? null}
              />
            </ListItem>
          </List>
        </div>
      </Drawer>
      <div
        style={{
          flexGrow: 1,
          marginLeft: drawerOpen && !isMobile ? "300px" : "0",
        }}
      >
        <Map
          sessionManager={sessionManager}
          asphalt_geojson={asphalt_geojson}
          building_geojson={building_geojson}
          concrete_geojson={concrete_geojson}
          edging_geojson={edging_geojson}
          grass_geojson={grass_geojson}
          low_vegetation_geojson={low_vegetation_geojson}
          medium_high_vegetation_geojson={medium_high_vegetation_geojson}
          muddy_geojson={muddy_geojson}
          natural_ground_geojson={natural_ground_geojson}
          water_geojson={water_geojson}
        />
      </div>
    </div>
  );
};

export default Dashboard;
